// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useContext, useEffect, useState } from 'react';

import { ViewerItem } from 'presentations/api/schema/user';
import { SubscriptionStateItem } from 'presentations/api/schema/subscription';
import {
  AdminTeamUserListItem,
  TeamUserInviteBody,
} from 'presentations/api/schema/team_user';

import { apiPath } from 'presentations/routes/path';
import { get, post } from 'infras/rest_client';
import * as tracker from 'presentations/views/helpers/analytics';
import { isEmail } from 'infras/validator';
import { useRequest } from 'presentations/views/hooks/request';
import Subscription from 'domains/models/subscription';
import { Role } from 'domains/models/team_user/role';
import {
  DashboardPortalContext,
  setErrorMessage,
  setIsOpenSeatEditForm,
  setIsOpenUserInviteForm,
  setSuccessMessage,
} from 'presentations/reducers/DashboardPortal';
import { PlanId } from 'domains/models/subscription/planId';
import { getDateTime } from 'infras/datetime';
import { TrialChannel } from 'domains/models/subscription/trial_channel';
import { Amplitude } from 'presentations/views/helpers/analytics/amplitude';

interface Props {
  viewer: ViewerItem;
  onInvited?: (newUsers: AdminTeamUserListItem[]) => void;
}

export function useUserInviteForm(props: Props) {
  const t = translateInComponent(translations);

  const { state, dispatch } = useContext(DashboardPortalContext);

  const [isInitLoading, setIsInitLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [emails, setEmails] = useState<string[]>([]);
  const [inputingEmail, setInputingEmail] = useState<string | null>(null);
  const [inputingRole, setInputingRole] = useState<Role>(Role.Admin);
  const [errors, setErrors] = useState<string[]>([]);
  const [errorEmailIndexList, setErrorEmailIndexList] = useState<number[]>([]);

  const subscriptionStateRequest = useRequest(
    !props.viewer
      ? null
      : apiPath(`/v1/teams/${props.viewer.team!.uid}/subscriptions/state`),
  );
  const subscriptionState: SubscriptionStateItem | null =
    subscriptionStateRequest.data;

  useEffect(() => {
    if (!state.isOpenUserInviteForm) return;
    setIsInitLoading(false);
  }, [state.isOpenUserInviteForm]);

  const teamUsersRequest = useRequest(
    state.isOpenUserInviteForm
      ? apiPath(`/v1/admin/teams/${props.viewer.team!.uid}/users`)
      : null,
  );
  const teamUsers: AdminTeamUserListItem[] = teamUsersRequest.data || [];
  const totalTeamUserCount = teamUsersRequest.totalCount;

  // エラー周り
  useEffect(() => {
    const newErrors: string[] = [];
    const newErrorEmailIndexList: number[] = [];
    let isErrorWrongEmailFormat: boolean = false;
    let isErrorAlreadyRegistered: boolean = false;
    emails.forEach((email, index) => {
      if (!isEmail(email)) {
        isErrorWrongEmailFormat = true;
        newErrorEmailIndexList.push(index);
      } else if (teamUsers.findIndex((user) => user.email === email) !== -1) {
        isErrorAlreadyRegistered = true;
        newErrorEmailIndexList.push(index);
      }
    });
    if (isErrorWrongEmailFormat) {
      newErrors.push(t('Wrong Email Format'));
    }
    if (isErrorAlreadyRegistered) {
      newErrors.push(t('Already Registered Email'));
    }
    setErrors(newErrors);
    setErrorEmailIndexList(newErrorEmailIndexList);
  }, [emails, teamUsers.length]);

  const addEmail = (text: string) => {
    const addEmails = text
      .split(/[\s,]/)
      .map((email) => email.trim())
      .filter((email) => email.length);
    setEmails([...emails, ...addEmails]);
  };

  const onPaste: React.ClipboardEventHandler<HTMLDivElement> = (e) => {
    const pasteText = e.clipboardData.getData('text');
    if (pasteText && pasteText.length > 0) {
      addEmail(pasteText);
      e.preventDefault();
    }
  };

  const onInputChangeEmail = async (e: any, value: string) => {
    setInputingEmail(value);
  };

  const onAddEmail = async (e: any) => {
    if (inputingEmail && inputingEmail.length > 0) {
      addEmail(inputingEmail);
      setInputingEmail(null);
    }
  };

  const onDeleteEmail = async (deleteIndex: number) => {
    const newEmails = [...emails];
    newEmails.splice(deleteIndex, 1);
    setEmails(newEmails);
  };

  const onInputChangeRole = async (e: SyntheticEvent<HTMLSelectElement>) => {
    const { value } = e.currentTarget;
    const role = Number(value);
    setInputingRole(role);
  };

  const onSubmitInvite = async (e: SyntheticEvent<HTMLButtonElement>) => {
    if (!emails.length) return;
    const data: TeamUserInviteBody = { emails: emails, role: inputingRole };
    setIsLoading(true);
    const result = await post(
      apiPath(`/v1/admin/teams/${props.viewer.team!.uid}/users/invite`),
      { data },
    );
    if (!result.isSuccess) {
      setIsLoading(false);
      dispatch(setErrorMessage(t('Invite Error')));
      return;
    }
    setIsLoading(false);
    dispatch(setIsOpenUserInviteForm(false));
    setEmails([]);
    dispatch(setSuccessMessage(t('Invited')));
    if (props.onInvited && Array.isArray(result.data)) {
      props.onInvited(result.data);
    }

    if (subscriptionState) {
      const quantity = subscriptionState.quantity + 1;
      subscriptionStateRequest.mutate({ ...subscriptionState, quantity });
      tracker.setEvent('admin invite user', {
        planId: subscriptionState.planId,
      });
      Amplitude.adminInviteUser({ planId: subscriptionState.planId });
    }
  };

  const onCancelInvite = async (e: SyntheticEvent<HTMLButtonElement>) => {
    dispatch(setIsOpenUserInviteForm(false));
    setEmails([]);
  };

  const onClickEditPlan = (e: any) => {
    dispatch(setIsOpenSeatEditForm(true));
  };

  // subscriptionStateが取れなければ追加可能と一旦判断する
  let canAddQuantity = true;
  let isTrialEnded = false;
  if (subscriptionState) {
    isTrialEnded =
      !subscriptionState.trialChannel ||
      (subscriptionState.trialChannel === TrialChannel.Jicoo &&
        getDateTime(subscriptionState.trialEndedAt) < getDateTime());
    canAddQuantity =
      Subscription.maxQuantity(subscriptionState.planId) >
      subscriptionState.quantity;
    if (
      subscriptionState.planId > PlanId.Free &&
      isTrialEnded &&
      subscriptionState.quantity <= teamUsers.length
    ) {
      canAddQuantity = false;
    }
  }

  return {
    state: {
      isOpen: state.isOpenUserInviteForm,
      isLoading: isLoading || isInitLoading,
      isTrialEnded,
      canAddQuantity,
      quantity: subscriptionState?.quantity || undefined,
      teamUsers,
      totalTeamUserCount,
      inputingEmail,
      inputingRole,
      emails,
      errors,
      errorEmailIndexList,
    },
    actions: {
      onPaste,
      onInputChangeEmail,
      onInputChangeRole,
      onAddEmail,
      onDeleteEmail,
      onClickEditPlan,
      onSubmit: onSubmitInvite,
      onCancel: onCancelInvite,
    },
  };
}
